import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, TrendingDown, Activity, Zap, BarChart3,
  AlertCircle, Loader, ChevronDown, ChevronUp, Target,
  MinusCircle, CheckCircle, XCircle, Info, RefreshCw
} from 'lucide-react';

// Mini Sparkline Chart Component
const SparklineChart = ({ data, color, height = 40, showArea = true }) => {
  if (!data || data.length < 2) return null;
  
  const values = data.map(d => parseFloat(d.value)).filter(v => !isNaN(v));
  if (values.length < 2) return null;
  
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  
  const width = 120;
  const padding = 2;
  
  const points = values.map((v, i) => {
    const x = padding + (i / (values.length - 1)) * (width - padding * 2);
    const y = height - padding - ((v - min) / range) * (height - padding * 2);
    return `${x},${y}`;
  }).join(' ');
  
  const areaPoints = `${padding},${height - padding} ${points} ${width - padding},${height - padding}`;
  
  // Determine trend color
  const trend = values[values.length - 1] > values[0] ? '#22c55e' : '#ef4444';
  const displayColor = color || trend;
  
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      {showArea && (
        <polygon
          points={areaPoints}
          fill={`${displayColor}20`}
        />
      )}
      <polyline
        points={points}
        fill="none"
        stroke={displayColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Current value dot */}
      <circle
        cx={width - padding}
        cy={height - padding - ((values[values.length - 1] - min) / range) * (height - padding * 2)}
        r="3"
        fill={displayColor}
      />
    </svg>
  );
};

// Larger Trend Chart Component
const TrendChart = ({ data, indicator, color }) => {
  if (!data || data.length < 2) {
    return (
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center', 
        color: '#64748b',
        background: 'rgba(15, 23, 42, 0.3)',
        borderRadius: '0.5rem'
      }}>
        Not enough historical data for chart
      </div>
    );
  }
  
  const values = data.map(d => parseFloat(d.value)).filter(v => !isNaN(v));
  const dates = data.map(d => d.date).slice(0, values.length);
  
  if (values.length < 2) return null;
  
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  
  const width = 100; // percentage
  const height = 120;
  const padding = { top: 10, bottom: 25, left: 10, right: 10 };
  
  const chartWidth = 100 - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  
  // Create SVG path
  const points = values.map((v, i) => {
    const x = padding.left + (i / (values.length - 1)) * chartWidth;
    const y = padding.top + chartHeight - ((v - min) / range) * chartHeight;
    return { x, y, value: v, date: dates[i] };
  });
  
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${height - padding.bottom} L ${padding.left} ${height - padding.bottom} Z`;
  
  // Reference lines for indicators
  const getRefLines = () => {
    if (indicator === 'RSI') {
      return [
        { value: 70, label: 'Overbought', color: '#ef4444' },
        { value: 30, label: 'Oversold', color: '#22c55e' }
      ];
    }
    if (indicator === 'STOCH' || indicator === 'MFI') {
      return [
        { value: 80, label: 'Overbought', color: '#ef4444' },
        { value: 20, label: 'Oversold', color: '#22c55e' }
      ];
    }
    if (indicator === 'CCI') {
      return [
        { value: 100, label: '+100', color: '#ef4444' },
        { value: -100, label: '-100', color: '#22c55e' }
      ];
    }
    if (indicator === 'ADX') {
      return [
        { value: 25, label: 'Strong Trend', color: '#f59e0b' }
      ];
    }
    return [];
  };
  
  const refLines = getRefLines();
  
  return (
    <div style={{ position: 'relative' }}>
      <svg 
        viewBox={`0 0 100 ${height}`} 
        style={{ width: '100%', height: `${height}px`, display: 'block' }}
        preserveAspectRatio="none"
      >
        {/* Grid lines */}
        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={height - padding.bottom} stroke="#334155" strokeWidth="0.5" />
        <line x1={padding.left} y1={height - padding.bottom} x2={100 - padding.right} y2={height - padding.bottom} stroke="#334155" strokeWidth="0.5" />
        
        {/* Reference lines */}
        {refLines.map((ref, i) => {
          if (ref.value < min || ref.value > max) return null;
          const y = padding.top + chartHeight - ((ref.value - min) / range) * chartHeight;
          return (
            <g key={i}>
              <line 
                x1={padding.left} 
                y1={y} 
                x2={100 - padding.right} 
                y2={y} 
                stroke={ref.color} 
                strokeWidth="0.5" 
                strokeDasharray="2,2" 
              />
            </g>
          );
        })}
        
        {/* Area fill */}
        <path d={areaPath} fill={`${color}15`} />
        
        {/* Line */}
        <path d={linePath} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
        
        {/* Current value dot */}
        <circle 
          cx={points[points.length - 1].x} 
          cy={points[points.length - 1].y} 
          r="2" 
          fill={color}
          style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))' }}
        />
      </svg>
      
      {/* Labels */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        fontSize: '0.65rem', 
        color: '#64748b',
        marginTop: '0.25rem'
      }}>
        <span>{dates[dates.length - 1]?.slice(5) || ''}</span>
        <span>{dates[0]?.slice(5) || ''}</span>
      </div>
      
      {/* Min/Max labels */}
      <div style={{
        position: 'absolute',
        top: '0.25rem',
        right: '0.5rem',
        fontSize: '0.6rem',
        color: '#64748b',
        textAlign: 'right'
      }}>
        <div>H: {max.toFixed(1)}</div>
        <div>L: {min.toFixed(1)}</div>
      </div>
    </div>
  );
};

// Indicator definitions
const INDICATORS = {
  // Momentum
  RSI: {
    name: 'RSI',
    fullName: 'Relative Strength Index',
    category: 'momentum',
    color: '#f59e0b',
    description: 'Measures overbought/oversold conditions',
    interpret: (val) => {
      if (val < 30) return { signal: 'OVERSOLD', type: 'bullish', color: '#22c55e', advice: 'Potential buying opportunity' };
      if (val > 70) return { signal: 'OVERBOUGHT', type: 'bearish', color: '#ef4444', advice: 'Consider taking profits' };
      return { signal: 'NEUTRAL', type: 'neutral', color: '#f59e0b', advice: 'No strong signal' };
    },
    getValue: (data) => data?.RSI ? parseFloat(data.RSI) : null
  },
  STOCH: {
    name: 'Stochastic',
    fullName: 'Stochastic Oscillator',
    category: 'momentum',
    color: '#ec4899',
    description: 'Compares closing price to range',
    interpret: (val) => {
      if (val < 20) return { signal: 'OVERSOLD', type: 'bullish', color: '#22c55e', advice: 'May bounce soon' };
      if (val > 80) return { signal: 'OVERBOUGHT', type: 'bearish', color: '#ef4444', advice: 'May pull back' };
      return { signal: 'NEUTRAL', type: 'neutral', color: '#f59e0b', advice: 'Mid-range' };
    },
    getValue: (data) => data?.SlowK ? parseFloat(data.SlowK) : null
  },
  CCI: {
    name: 'CCI',
    fullName: 'Commodity Channel Index',
    category: 'momentum',
    color: '#f97316',
    description: 'Identifies cyclical trends',
    interpret: (val) => {
      if (val < -100) return { signal: 'OVERSOLD', type: 'bullish', color: '#22c55e', advice: 'Below normal range' };
      if (val > 100) return { signal: 'OVERBOUGHT', type: 'bearish', color: '#ef4444', advice: 'Above normal range' };
      return { signal: 'NEUTRAL', type: 'neutral', color: '#f59e0b', advice: 'Normal range' };
    },
    getValue: (data) => data?.CCI ? parseFloat(data.CCI) : null
  },
  WILLR: {
    name: 'Williams %R',
    fullName: 'Williams Percent Range',
    category: 'momentum',
    color: '#fb923c',
    description: 'Momentum indicator',
    interpret: (val) => {
      if (val < -80) return { signal: 'OVERSOLD', type: 'bullish', color: '#22c55e', advice: 'Near lows' };
      if (val > -20) return { signal: 'OVERBOUGHT', type: 'bearish', color: '#ef4444', advice: 'Near highs' };
      return { signal: 'NEUTRAL', type: 'neutral', color: '#f59e0b', advice: 'Mid-range' };
    },
    getValue: (data) => data?.WILLR ? parseFloat(data.WILLR) : null
  },
  MFI: {
    name: 'MFI',
    fullName: 'Money Flow Index',
    category: 'momentum',
    color: '#fbbf24',
    description: 'Volume-weighted RSI',
    interpret: (val) => {
      if (val < 20) return { signal: 'OVERSOLD', type: 'bullish', color: '#22c55e', advice: 'Heavy selling' };
      if (val > 80) return { signal: 'OVERBOUGHT', type: 'bearish', color: '#ef4444', advice: 'Heavy buying' };
      return { signal: 'NEUTRAL', type: 'neutral', color: '#f59e0b', advice: 'Normal flow' };
    },
    getValue: (data) => data?.MFI ? parseFloat(data.MFI) : null
  },
  // Trend
  MACD: {
    name: 'MACD',
    fullName: 'Moving Avg Convergence Divergence',
    category: 'trend',
    color: '#22c55e',
    description: 'Trend direction & momentum',
    interpret: (val, data) => {
      const macd = parseFloat(data?.MACD || 0);
      const signal = parseFloat(data?.MACD_Signal || 0);
      if (macd > signal) return { signal: 'BULLISH', type: 'bullish', color: '#22c55e', advice: 'Upward momentum' };
      return { signal: 'BEARISH', type: 'bearish', color: '#ef4444', advice: 'Downward momentum' };
    },
    getValue: (data) => data?.MACD ? parseFloat(data.MACD) : null
  },
  ADX: {
    name: 'ADX',
    fullName: 'Average Directional Index',
    category: 'trend',
    color: '#06b6d4',
    description: 'Trend strength (not direction)',
    interpret: (val) => {
      if (val > 50) return { signal: 'VERY STRONG', type: 'neutral', color: '#22c55e', advice: 'Powerful trend' };
      if (val > 25) return { signal: 'STRONG', type: 'neutral', color: '#06b6d4', advice: 'Clear trend' };
      return { signal: 'WEAK', type: 'neutral', color: '#f59e0b', advice: 'No trend/ranging' };
    },
    getValue: (data) => data?.ADX ? parseFloat(data.ADX) : null
  },
  AROON: {
    name: 'Aroon',
    fullName: 'Aroon Indicator',
    category: 'trend',
    color: '#0ea5e9',
    description: 'Trend changes & strength',
    interpret: (val, data) => {
      const up = parseFloat(data?.['Aroon Up'] || 0);
      const down = parseFloat(data?.['Aroon Down'] || 0);
      if (up > 70 && up > down) return { signal: 'UPTREND', type: 'bullish', color: '#22c55e', advice: 'Strong uptrend' };
      if (down > 70 && down > up) return { signal: 'DOWNTREND', type: 'bearish', color: '#ef4444', advice: 'Strong downtrend' };
      return { signal: 'NO TREND', type: 'neutral', color: '#f59e0b', advice: 'No clear direction' };
    },
    getValue: (data) => data?.['Aroon Up'] ? parseFloat(data['Aroon Up']) : null
  },
  // Volatility
  BBANDS: {
    name: 'Bollinger',
    fullName: 'Bollinger Bands',
    category: 'volatility',
    color: '#8b5cf6',
    description: 'Volatility & potential reversals',
    interpret: (val, data, price) => {
      const upper = parseFloat(data?.['Real Upper Band'] || 0);
      const lower = parseFloat(data?.['Real Lower Band'] || 0);
      if (price && price > upper) return { signal: 'ABOVE UPPER', type: 'bearish', color: '#ef4444', advice: 'May pull back' };
      if (price && price < lower) return { signal: 'BELOW LOWER', type: 'bullish', color: '#22c55e', advice: 'May bounce' };
      return { signal: 'WITHIN BANDS', type: 'neutral', color: '#f59e0b', advice: 'Normal range' };
    },
    getValue: (data) => data?.['Real Middle Band'] ? parseFloat(data['Real Middle Band']) : null
  },
  ATR: {
    name: 'ATR',
    fullName: 'Average True Range',
    category: 'volatility',
    color: '#a78bfa',
    description: 'Market volatility measure',
    interpret: (val) => {
      return { signal: 'VOLATILITY', type: 'neutral', color: '#a78bfa', advice: `$${val?.toFixed(2) || 0} avg range` };
    },
    getValue: (data) => data?.ATR ? parseFloat(data.ATR) : null
  },
  // Volume
  OBV: {
    name: 'OBV',
    fullName: 'On Balance Volume',
    category: 'volume',
    color: '#14b8a6',
    description: 'Volume-based price prediction',
    interpret: (val, data, price, prevData) => {
      const current = parseFloat(data?.OBV || 0);
      const prev = parseFloat(prevData?.OBV || 0);
      if (current > prev) return { signal: 'ACCUMULATION', type: 'bullish', color: '#22c55e', advice: 'Buying pressure' };
      return { signal: 'DISTRIBUTION', type: 'bearish', color: '#ef4444', advice: 'Selling pressure' };
    },
    getValue: (data) => data?.OBV ? parseFloat(data.OBV) / 1000000 : null
  },
  AD: {
    name: 'A/D Line',
    fullName: 'Accumulation/Distribution',
    category: 'volume',
    color: '#2dd4bf',
    description: 'Cumulative money flow',
    interpret: (val, data, price, prevData) => {
      const current = parseFloat(data?.['Chaikin A/D'] || 0);
      const prev = parseFloat(prevData?.['Chaikin A/D'] || 0);
      if (current > prev) return { signal: 'ACCUMULATION', type: 'bullish', color: '#22c55e', advice: 'Money flowing in' };
      return { signal: 'DISTRIBUTION', type: 'bearish', color: '#ef4444', advice: 'Money flowing out' };
    },
    getValue: (data) => data?.['Chaikin A/D'] ? parseFloat(data['Chaikin A/D']) / 1000000 : null
  }
};

const INDICATOR_IDS = Object.keys(INDICATORS);

// Category info
const CATEGORIES = {
  momentum: { name: 'Momentum', icon: Zap, color: '#f59e0b' },
  trend: { name: 'Trend', icon: TrendingUp, color: '#22c55e' },
  volatility: { name: 'Volatility', icon: Activity, color: '#8b5cf6' },
  volume: { name: 'Volume', icon: BarChart3, color: '#14b8a6' }
};

// Main Component
const TechnicalAnalysisTab = ({ symbol, currentPrice }) => {
  const [indicators, setIndicators] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedIndicator, setExpandedIndicator] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [overallSignal, setOverallSignal] = useState(null);
  
  const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

  // Fetch all indicators
  const fetchIndicators = useCallback(async () => {
    if (!symbol) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const indicatorData = {};
      
      // Fetch indicators sequentially with small delay to avoid rate limits
      for (let i = 0; i < INDICATOR_IDS.length; i++) {
        const id = INDICATOR_IDS[i];
        
        try {
          const response = await fetch(
            `${API_URL}/technical/${symbol}/${id.toLowerCase()}?interval=daily`
          );
          const data = await response.json();
          
          if (data.success !== false && data.data && data.data.length > 0) {
            const datePattern = new RegExp('^\\d{4}-\\d{2}-\\d{2}');
            indicatorData[id] = {
              current: data.data[0],
              previous: data.data[1] || null,
              history: data.data.slice(0, 20).map(d => {
                const keys = Object.keys(d);
                const dateKey = keys.find(k => datePattern.test(k)) || '';
                return {
                  date: dateKey,
                  value: INDICATORS[id].getValue(d),
                  raw: d
                };
              }).filter(d => d.value !== null).reverse()
            };
          }
        } catch (err) {
          console.error(`Failed to fetch ${id}:`, err);
        }
        
        // Small delay between requests
        if (i < INDICATOR_IDS.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      setIndicators(indicatorData);
      calculateOverallSignal(indicatorData);
      
    } catch (err) {
      console.error('Error fetching indicators:', err);
      setError('Failed to load technical indicators');
    } finally {
      setLoading(false);
    }
  }, [symbol, API_URL]);

  useEffect(() => {
    fetchIndicators();
  }, [fetchIndicators]);

  // Calculate overall signal
  const calculateOverallSignal = (data) => {
    let bullish = 0, bearish = 0, neutral = 0;
    const signals = [];
    
    INDICATOR_IDS.forEach(id => {
      const indicator = INDICATORS[id];
      const indicatorData = data[id];
      if (!indicatorData?.current) return;
      
      const value = indicator.getValue(indicatorData.current);
      if (value === null) return;
      
      const interp = indicator.interpret(value, indicatorData.current, currentPrice, indicatorData.previous);
      signals.push({ id, name: indicator.name, ...interp, value });
      
      if (interp.type === 'bullish') bullish++;
      else if (interp.type === 'bearish') bearish++;
      else neutral++;
    });
    
    const total = bullish + bearish + neutral;
    const bullishPct = total > 0 ? (bullish / total) * 100 : 0;
    const bearishPct = total > 0 ? (bearish / total) * 100 : 0;
    
    let signal = 'NEUTRAL', color = '#f59e0b', advice = 'Mixed signals';
    
    if (bullishPct >= 60) {
      signal = 'STRONG BUY';
      color = '#22c55e';
      advice = `${bullish} of ${total} indicators bullish (${bullishPct.toFixed(0)}%)`;
    } else if (bullishPct >= 40 && bullish > bearish) {
      signal = 'BULLISH';
      color = '#4ade80';
      advice = `${bullish} bullish vs ${bearish} bearish`;
    } else if (bearishPct >= 60) {
      signal = 'STRONG SELL';
      color = '#ef4444';
      advice = `${bearish} of ${total} indicators bearish (${bearishPct.toFixed(0)}%)`;
    } else if (bearishPct >= 40 && bearish > bullish) {
      signal = 'BEARISH';
      color = '#f87171';
      advice = `${bearish} bearish vs ${bullish} bullish`;
    } else {
      advice = `${bullish} bullish, ${bearish} bearish, ${neutral} neutral`;
    }
    
    setOverallSignal({ signal, color, advice, bullish, bearish, neutral, total, bullishPct, bearishPct, signals });
  };

  // Filter indicators by category
  const filteredIndicators = INDICATOR_IDS.filter(id => 
    activeCategory === 'all' || INDICATORS[id].category === activeCategory
  );

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <Loader size={40} color="#14b8a6" style={{ animation: 'spin 1s linear infinite', marginBottom: '1rem' }} />
        <p style={{ color: '#94a3b8' }}>Loading technical indicators for {symbol}...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <AlertCircle size={32} color="#ef4444" style={{ marginBottom: '0.5rem' }} />
        <p style={{ color: '#f87171' }}>{error}</p>
        <button
          onClick={fetchIndicators}
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1rem',
            background: '#0f766e',
            border: 'none',
            borderRadius: '0.5rem',
            color: 'white',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Overall Signal Summary */}
      {overallSignal && (
        <div style={{
          background: `linear-gradient(135deg, ${overallSignal.color}15 0%, ${overallSignal.color}05 100%)`,
          border: `2px solid ${overallSignal.color}40`,
          borderRadius: '1rem',
          padding: '1.25rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{
                background: `${overallSignal.color}25`,
                borderRadius: '0.75rem',
                padding: '0.75rem',
                color: overallSignal.color
              }}>
                {overallSignal.signal.includes('BUY') || overallSignal.signal === 'BULLISH' ? 
                  <TrendingUp size={24} /> : 
                  overallSignal.signal.includes('SELL') || overallSignal.signal === 'BEARISH' ?
                  <TrendingDown size={24} /> : <MinusCircle size={24} />
                }
              </div>
              <div>
                <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Technical Signal</div>
                <div style={{ color: overallSignal.color, fontSize: '1.5rem', fontWeight: '700' }}>
                  {overallSignal.signal}
                </div>
              </div>
            </div>
            
            {/* Signal breakdown */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              background: 'rgba(15, 23, 42, 0.5)',
              padding: '0.75rem 1rem',
              borderRadius: '0.75rem'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#22c55e', fontSize: '1.25rem', fontWeight: '700' }}>{overallSignal.bullish}</div>
                <div style={{ color: '#64748b', fontSize: '0.7rem' }}>Bullish</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#f59e0b', fontSize: '1.25rem', fontWeight: '700' }}>{overallSignal.neutral}</div>
                <div style={{ color: '#64748b', fontSize: '0.7rem' }}>Neutral</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#ef4444', fontSize: '1.25rem', fontWeight: '700' }}>{overallSignal.bearish}</div>
                <div style={{ color: '#64748b', fontSize: '0.7rem' }}>Bearish</div>
              </div>
            </div>
          </div>
          
          {/* Progress bar */}
          <div style={{ marginTop: '1rem' }}>
            <div style={{
              height: '8px',
              background: '#1e293b',
              borderRadius: '4px',
              overflow: 'hidden',
              display: 'flex'
            }}>
              <div style={{ width: `${overallSignal.bullishPct}%`, background: '#22c55e' }} />
              <div style={{ width: `${100 - overallSignal.bullishPct - overallSignal.bearishPct}%`, background: '#f59e0b' }} />
              <div style={{ width: `${overallSignal.bearishPct}%`, background: '#ef4444' }} />
            </div>
            <div style={{ marginTop: '0.5rem', color: '#94a3b8', fontSize: '0.8rem' }}>
              {overallSignal.advice}
            </div>
          </div>
        </div>
      )}

      {/* Category Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '0.5rem', 
        marginBottom: '1rem',
        overflowX: 'auto',
        paddingBottom: '0.5rem'
      }}>
        <button
          onClick={() => setActiveCategory('all')}
          style={{
            padding: '0.5rem 1rem',
            background: activeCategory === 'all' ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(30, 41, 59, 0.8)',
            border: 'none',
            borderRadius: '0.5rem',
            color: 'white',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: activeCategory === 'all' ? '600' : '400',
            whiteSpace: 'nowrap'
          }}
        >
          All ({INDICATOR_IDS.length})
        </button>
        {Object.entries(CATEGORIES).map(([key, cat]) => {
          const count = INDICATOR_IDS.filter(id => INDICATORS[id].category === key).length;
          const Icon = cat.icon;
          return (
            <button
              key={key}
              onClick={() => setActiveCategory(key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.5rem 0.75rem',
                background: activeCategory === key ? `${cat.color}30` : 'rgba(30, 41, 59, 0.8)',
                border: activeCategory === key ? `1px solid ${cat.color}` : '1px solid transparent',
                borderRadius: '0.5rem',
                color: activeCategory === key ? cat.color : '#94a3b8',
                cursor: 'pointer',
                fontSize: '0.85rem',
                whiteSpace: 'nowrap'
              }}
            >
              <Icon size={14} />
              {cat.name}
            </button>
          );
        })}
        
        <button
          onClick={fetchIndicators}
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            padding: '0.5rem 0.75rem',
            background: 'rgba(15, 118, 110, 0.3)',
            border: '1px solid #0f766e',
            borderRadius: '0.5rem',
            color: '#14b8a6',
            cursor: 'pointer',
            fontSize: '0.85rem',
            whiteSpace: 'nowrap'
          }}
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {/* Indicators Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '1rem'
      }}>
        {filteredIndicators.map(id => {
          const indicator = INDICATORS[id];
          const data = indicators[id];
          if (!data?.current) return null;
          
          const value = indicator.getValue(data.current);
          if (value === null) return null;
          
          const interp = indicator.interpret(value, data.current, currentPrice, data.previous);
          const isExpanded = expandedIndicator === id;
          const CatIcon = CATEGORIES[indicator.category].icon;
          
          return (
            <div
              key={id}
              style={{
                background: 'rgba(30, 41, 59, 0.6)',
                border: `1px solid ${interp.color}30`,
                borderRadius: '0.75rem',
                overflow: 'hidden'
              }}
            >
              {/* Header */}
              <div style={{ padding: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{
                      background: `${indicator.color}20`,
                      padding: '0.4rem',
                      borderRadius: '0.4rem',
                      color: indicator.color
                    }}>
                      <CatIcon size={16} />
                    </div>
                    <div>
                      <div style={{ color: '#e2e8f0', fontWeight: '600', fontSize: '0.95rem' }}>
                        {indicator.name}
                      </div>
                      <div style={{ color: '#64748b', fontSize: '0.7rem' }}>
                        {indicator.description}
                      </div>
                    </div>
                  </div>
                  
                  {/* Signal badge */}
                  <div style={{
                    background: `${interp.color}20`,
                    color: interp.color,
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.375rem',
                    fontSize: '0.7rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}>
                    {interp.type === 'bullish' ? <TrendingUp size={12} /> : 
                     interp.type === 'bearish' ? <TrendingDown size={12} /> : 
                     <MinusCircle size={12} />}
                    {interp.signal}
                  </div>
                </div>
                
                {/* Value and Sparkline */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: '#64748b', fontSize: '0.7rem', marginBottom: '0.25rem' }}>Current Value</div>
                    <div style={{ color: interp.color, fontSize: '1.25rem', fontWeight: '700' }}>
                      {typeof value === 'number' ? value.toFixed(2) : value}
                    </div>
                  </div>
                  
                  {data.history && data.history.length > 1 && (
                    <SparklineChart data={data.history} color={indicator.color} />
                  )}
                </div>
                
                {/* Advice */}
                <div style={{
                  marginTop: '0.75rem',
                  padding: '0.5rem',
                  background: `${interp.color}10`,
                  borderLeft: `3px solid ${interp.color}`,
                  borderRadius: '0 0.25rem 0.25rem 0',
                  fontSize: '0.8rem',
                  color: '#cbd5e1'
                }}>
                  {interp.advice}
                </div>
              </div>
              
              {/* Expandable Chart */}
              <button
                onClick={() => setExpandedIndicator(isExpanded ? null : id)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  background: 'rgba(15, 23, 42, 0.5)',
                  border: 'none',
                  borderTop: '1px solid #334155',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  fontSize: '0.75rem'
                }}
              >
                {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                {isExpanded ? 'Hide' : 'Show'} Trend Chart
              </button>
              
              {isExpanded && (
                <div style={{ padding: '1rem', background: 'rgba(15, 23, 42, 0.3)' }}>
                  <div style={{ color: '#94a3b8', fontSize: '0.75rem', marginBottom: '0.5rem' }}>
                    {indicator.fullName} - 20 Day Trend
                  </div>
                  <TrendChart data={data.history} indicator={id} color={indicator.color} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Disclaimer */}
      <div style={{
        marginTop: '1.5rem',
        padding: '1rem',
        background: 'rgba(251, 191, 36, 0.1)',
        border: '1px solid rgba(251, 191, 36, 0.3)',
        borderRadius: '0.75rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <AlertCircle size={16} color="#fbbf24" />
          <span style={{ color: '#fbbf24', fontWeight: '600', fontSize: '0.85rem' }}>Educational Purposes Only</span>
        </div>
        <p style={{ color: '#fcd34d', margin: 0, fontSize: '0.8rem', lineHeight: '1.5' }}>
          Technical indicators are analytical tools, not guarantees of future performance. 
          Always conduct your own research and consider consulting a financial advisor.
        </p>
      </div>
    </div>
  );
};

export default TechnicalAnalysisTab;